import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FileText, X, Download } from "lucide-react";
import { UserData } from "../../types/user-data.type";
import useApi from "../../hooks/api/useApi";
import IranPlateInput from "../admin/IranPlateInput";
import PdfViewer from "../PdfViewer";

interface FileItem {
    id: string;
    url: string;
    name: string;
    type: string;
    title:string;
}

interface Insurance {
    id: string;
    title: string;
    code: string;
    description: string;
    files: FileItem[];
}

const BASE_URL = process.env.REACT_APP_BASE_URL;

function FileModal({ file, onClose }: { file: FileItem; onClose: () => void }) {
    const fileUrl = BASE_URL + '/' + file.url;
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";
    // Close on backdrop click
    const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    // Close on Escape
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
            onClick={handleBackdrop}
        >
            <div className="relative bg-white rounded-xl shadow-2xl flex flex-col max-w-4xl w-full h-[90vh]">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b">
                    <span className="text-sm font-medium text-gray-700 truncate">{file.name}</span>
                    <div className="flex items-center gap-2 ml-4 shrink-0">
                        <a
                            href={fileUrl}
                            download={file.name}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition"
                            title="Download"
                        >
                            <Download className="w-4 h-4" />
                        </a>
                        <button
                            onClick={onClose}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition"
                            title="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto flex items-center justify-center p-4 min-h-0">
                    {isImage && (
                        <img
                            src={fileUrl}
                            alt={file.name}
                            className="max-w-full max-h-full object-contain rounded"
                        />
                    )}
                    {isPdf && (
                        <PdfViewer url={fileUrl} />
                    )}
                    {!isImage && !isPdf && (
                        <div className="flex flex-col items-center gap-3 text-gray-400 py-12">
                            <FileText className="w-16 h-16" />
                            <p className="text-sm">Preview not available for this file type.</p>
                            <a
                                href={fileUrl}
                                download={file.name}
                                className="text-sm text-blue-600 hover:underline"
                            >
                                Download file
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function InsurancesDetailPage() {
    const { id } = useParams<{ id: string }>();
    const [insurance, setInsurance] = useState<Insurance | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
    const user = localStorage.getItem("user");
    const userData: UserData = JSON.parse(user ?? '');
    const { request } = useApi();
    const navigate = useNavigate();

    useEffect(() => {
        const params = {
            method: "Get",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + userData.authToken
            },
        };

        request(`/insurances/${id}`, params, (result) => {
            setInsurance(result.payload);
        }).finally(() => setLoading(false));
    }, [id]);

    if (loading) return <div className="p-6">Loading...</div>;
    if (!insurance) return <div className="p-6">Not found.</div>;

    return (
        <div className="p-6 space-y-6">
            {userData.roles.includes('Admin') &&
                <div className="flex gap-2">
                    <button
                        onClick={() => navigate(`/admin/insurances/${id}/edit`)}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        ویرایش
                    </button>
                </div>}
            <div className="space-y-2">
                <h1 className="text-2xl font-bold">{insurance.title}</h1>
                <IranPlateInput valueText={insurance.code} readOnly={true} onChange={() => { }}></IranPlateInput>
                <p className="text-gray-700">توضیحات :{insurance.description}</p>
            </div>

            {insurance.files?.length > 0 && (
                <div>
                    <h2 className="text-lg font-semibold mb-3">لیست فایلها</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {insurance.files.map((file) => (
                            <button
                                key={file.url}
                                onClick={() => setSelectedFile(file)}
                                className="flex flex-col items-center border rounded-lg p-2 hover:bg-gray-50 transition"
                            >
                                {file.type.startsWith("image/") ? (
                                    <img
                                        src={BASE_URL + '/' + file.url}
                                        alt={file.name}
                                        className="w-full h-24 object-cover rounded"
                                    />
                                ) : (
                                    <div className="w-full h-24 flex items-center justify-center bg-gray-100 rounded">
                                        <FileText className="w-10 h-10 text-gray-400" />
                                    </div>
                                )}
                                <span className="mt-1 text-xs text-center text-gray-600 truncate w-full">
                                    {file.name}
                                </span>
                                <span className="mt-1 text-xs text-center text-gray-600 truncate w-full">
                                    {file.title}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {selectedFile && (
                <FileModal file={selectedFile} onClose={() => setSelectedFile(null)} />
            )}
        </div>
    );
}
